import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import ThemeSelector from '../ThemeSelector';
import { selectTheme } from '../../store/themeSlice';
import { selectIsAuthenticated, selectCurrentUser, logoutUser } from '../../store/authSlice';
import './Navbar.css';

const NAV_LINKS = [
  { to: '/database', label: 'Pokédex' },
  { to: '/competitive', label: 'Competitive' },
  {
    label: 'Definitions',
    dropdown: [
      { to: '/abilities', label: 'Abilities' },
      { to: '/moves',     label: 'Moves'     },
      { to: '/items',     label: 'Items'     },
    ],
  },
  { to: '/types',     label: 'Charts'     },
  { to: '/quizzes',   label: 'Quizzes'    },
  { to: '/quickcalc', label: 'Quick Calc' },
];

function Navbar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentTheme = useSelector(selectTheme);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectCurrentUser);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();

  const handleLogout = async () => {
    await dispatch(logoutUser());
    setUserMenuOpen(false);
    navigate('/');
  };

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    document.body.style.overflow = '';
  }, []);

  useEffect(() => { closeMenu(); }, [location, closeMenu]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const toggleMenu = () => setMenuOpen(open => !open);

  // Is any dropdown child the active route?
  const isDropdownActive = (dropdown) =>
    dropdown.some(({ to }) => location.pathname.startsWith(to));

  return (
    <nav className="navbar">
      <div className="navbar-content">
        <div className="navbar-brand">
          <Link to="/">POKéCOMP</Link>
        </div>

        {/* Desktop nav links */}
        <div className="right-content">
          <div className="navbar-links">
            {NAV_LINKS.map((link) => {
              if (link.dropdown) {
                return (
                  <div key={link.label} className={`nav-dropdown${isDropdownActive(link.dropdown) ? ' is-active' : ''}`}>
                    <span className="nav-dropdown-trigger">
                      {link.label}
                      <svg className="nav-dropdown-caret" viewBox="0 0 10 6" width="10" height="6" fill="currentColor">
                        <path d="M0 0l5 6 5-6z"/>
                      </svg>
                    </span>
                    <div className="nav-dropdown-menu">
                      {link.dropdown.map(({ to, label }) => (
                        <Link key={to} to={to}>{label}</Link>
                      ))}
                    </div>
                  </div>
                );
              }
              return <Link key={link.to} to={link.to}>{link.label}</Link>;
            })}
          </div>
          <ThemeSelector currentTheme={currentTheme} />

          {/* Auth section — desktop */}
          {isAuthenticated ? (
            <div className="nav-user-menu">
              <button
                className="nav-user-trigger"
                onClick={() => setUserMenuOpen(o => !o)}
                aria-expanded={userMenuOpen}
              >
                <span className="nav-user-avatar">
                  {user?.avatar
                    ? <img src={user.avatar} alt={user.displayName} referrerPolicy="no-referrer" />
                    : user?.displayName?.[0]?.toUpperCase() || '?'
                  }
                </span>
                <span className="nav-user-name">{user?.displayName}</span>
                <svg viewBox="0 0 10 6" width="10" height="6" fill="currentColor">
                  <path d="M0 0l5 6 5-6z"/>
                </svg>
              </button>
              {userMenuOpen && (
                <div className="nav-user-dropdown">
                  <Link to="/profile" onClick={() => setUserMenuOpen(false)}>My Profile</Link>
                  <button onClick={handleLogout}>Log Out</button>
                </div>
              )}
            </div>
          ) : (
            <div className="nav-auth-links">
              <Link to="/login" className="nav-auth-login">Sign In</Link>
              <Link to="/signup" className="nav-auth-signup">Sign Up</Link>
            </div>
          )}
        </div>

        {/* Hamburger button — mobile only */}
        <button
          className={`navbar-hamburger${menuOpen ? ' is-open' : ''}`}
          onClick={toggleMenu}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
        >
          <span /><span /><span />
        </button>
      </div>

      {/* Mobile overlay */}
      {menuOpen && (
        <div className="navbar-mobile-overlay" onClick={closeMenu}>
          <div className="navbar-mobile-menu" onClick={e => e.stopPropagation()}>
            <ThemeSelector currentTheme={currentTheme} />
            {NAV_LINKS.map((link) => {
              if (link.dropdown) {
                return (
                  <React.Fragment key={link.label}>
                    <span className="navbar-mobile-section">{link.label}</span>
                    {link.dropdown.map(({ to, label }) => (
                      <Link key={to} to={to} className="navbar-mobile-link navbar-mobile-sublink" onClick={closeMenu}>
                        {label}
                      </Link>
                    ))}
                  </React.Fragment>
                );
              }
              return (
                <Link key={link.to} to={link.to} className="navbar-mobile-link" onClick={closeMenu}>
                  {link.label}
                </Link>
              );
            })}
            <div className="navbar-mobile-divider" />
            {isAuthenticated ? (
              <>
                <Link to="/profile" className="navbar-mobile-link" onClick={closeMenu}>My Profile</Link>
                <button className="navbar-mobile-link navbar-mobile-logout" onClick={() => { handleLogout(); closeMenu(); }}>
                  Log Out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="navbar-mobile-link" onClick={closeMenu}>Sign In</Link>
                <Link to="/signup" className="navbar-mobile-link" onClick={closeMenu}>Sign Up</Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
